import React, { useEffect } from "react";
import { useAppContext } from "../../../hooks/useAppContext";
import { useProductVariantCode } from "../../../hooks/useProductVariantCode";
import { showDialogAsync } from "../../dialog-queue";
import { IFrameDialog } from "../../dialog/iframe";

interface IAiFilterFinishedEventDetail {
	selection: { fileId: string }[];
}

/** Return selected photos, undefined on close/cancel */
export const showAiFilterDialogAsync = () =>
	showDialogAsync<eXpress.core.Photo[] | undefined>(({ resolve }, { isOpen }) => {
		const {
			context: { homeUrl, language },
			editor,
			client,
		} = useAppContext();
		const productVariantCode = useProductVariantCode();

		useEffect(() => {
			const onImportFinished = ((event: CustomEvent<IAiFilterFinishedEventDetail | null>) => {
				const selection = event.detail?.selection;
				if (!selection || !editor.definition) {
					resolve(undefined);
					return;
				}

				const fileIds = selection.map((photo) => photo.fileId);

				void client.getPhotoImageRefsAsync(fileIds).then((photoImageRefs) => {
					const photos = Object.values(photoImageRefs).map((photoImageRef) => {
						const fileId = (photoImageRef as eXpress.client.SmartPhotoSourceItem)
							.fileId;

						// When there is already a photo asset for that file ID, it is reused
						return new eXpress.core.Photo(photoImageRef, editor.assets.photos, fileId);
					});

					resolve(photos);
				});
			}) as EventListener;

			document.addEventListener("importPhotosFinished", onImportFinished);

			const onClose = (event: MessageEvent) => {
				if (!event.data || event.data.command !== "CloseImportPhotosPopUp") return;
				resolve(undefined);
			};
			// iframe has its own close icon that triggers a message with command: 'CloseImportPhotosPopUp'
			window.addEventListener("message", onClose);

			return () => {
				document.removeEventListener("importPhotosFinished", onImportFinished);
				window.removeEventListener("message", onClose);
			};
		}, [client, editor, resolve]);

		const url = `${homeUrl}${language}/ai-filter?pvc=${productVariantCode ?? ""}`;
		// No onClose prop: IFrameDialog's close icon only renders when onClose is defined.
		// The iframe handles its own close button via the 'CloseImportPhotosPopUp' message above.
		return <IFrameDialog isOpen={isOpen} url={url} iframeTitle="ai-filter" />;
	});
